/**
 * Email -> 6-digit code sign-in, for whoever opens a jam. No passwords. The
 * marketing box is separate and unchecked (Israeli spam law §30A / GDPR).
 */
import { useEffect, useState } from 'react';
import { CODE_LENGTH, isEmail, MARKETING_CONSENT, normEmail, type Me } from '../shared/account.ts';
import { api } from './api.ts';

export function SignIn({ onDone, title = 'Sign in to host' }: { onDone: (me: Me) => void; title?: string }) {
  const [email, setEmail] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Seconds until "Send again" works (the server allows one code per address every 30 s). */
  const [wait, setWait] = useState(0);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const send = () =>
    run(async () => {
      await api.sendCode(normEmail(email));
      setStep('code');
      setCode('');
      setWait(30);
    });

  const verify = (c = code) =>
    run(async () => {
      onDone(await api.verify(normEmail(email), c, marketing));
    });

  return (
    <div className="signin">
      <h2>{title}</h2>
      {step === 'email' ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isEmail(normEmail(email))) void send();
            else setError('That doesn’t look like an email address. Check it and try again.');
          }}
        >
          <p className="hint">Only the person who opens a jam needs an account. Everyone else just scans the QR code.</p>
          <label className="field">
            <span>Email</span>
            <input type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus />
          </label>
          <label className="check">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
            <span>{MARKETING_CONSENT}</span>
          </label>
          <button className="btn btn-primary btn-xl" type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Email me a code'}
          </button>
          <p className="fine">
            We keep your email and nothing else about you. <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
          </p>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === CODE_LENGTH) void verify();
          }}
        >
          <p className="hint">
            We sent a {CODE_LENGTH}-digit code to <b>{normEmail(email)}</b>. It works for 10 minutes. Check spam if it's not there.
          </p>
          <input
            className="code-input"
            value={code}
            onChange={(e) => {
              const c = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
              setCode(c);
              if (c.length === CODE_LENGTH && !busy) void verify(c);
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="••••••"
            aria-label="Code from the email"
            autoFocus
          />
          <button className="btn btn-primary btn-xl" type="submit" disabled={busy || code.length !== CODE_LENGTH}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          <div className="row gap signin-alt">
            <button type="button" className="btn btn-sm" disabled={busy || wait > 0} onClick={() => void send()}>
              {wait > 0 ? `Send again in 0:${String(wait).padStart(2, '0')}` : 'Send again'}
            </button>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setStep('email')}>
              Different email
            </button>
          </div>
        </form>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
